import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Container, LinearProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrivateRoute from 'Components/Route/PrivateRoute';
import RecipeForm from 'Components/RecipeForm/RecipeForm';
import ButtonAppBar from 'Layouts/ButtonAppBar/ButtonAppBar';
import { useNotification } from 'Hooks/useNotification';
import { recipeService } from 'Services/recipeService';
import { CreateRecipeInput, Recipe } from 'Types/api';
import { ROUTES } from 'Utils/routes';

const EditRecipe = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRecipe = useCallback(async () => {
    if (!id) return;
    try {
      const data = await recipeService.get(id);
      setRecipe(data);
    } catch {
      setError('Recipe not found.');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRecipe();
  }, [fetchRecipe]);

  const handleSubmit = async (input: CreateRecipeInput) => {
    if (!id) return;
    try {
      await recipeService.update(id, input);
      showNotification('Recipe updated successfully!', 'success');
      navigate(`/recipes/${id}`);
    } catch {
      showNotification('Failed to update recipe. Please try again.');
    }
  };

  const handleAutoSave = useCallback(
    async (input: CreateRecipeInput) => {
      if (!id) return;
      await recipeService.update(id, input);
    },
    [id],
  );

  if (isLoading) {
    return (
      <PrivateRoute>
        <ButtonAppBar />
        <Box sx={{ width: '100%' }}>
          <LinearProgress color='secondary' />
        </Box>
      </PrivateRoute>
    );
  }

  if (error || !recipe) {
    return (
      <PrivateRoute>
        <ButtonAppBar />
        <Container maxWidth='md'>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant='h6' color='text.secondary' gutterBottom>
              {error || 'Recipe not found'}
            </Typography>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROUTES.DASHBOARD)}>
              Back to Recipes
            </Button>
          </Box>
        </Container>
      </PrivateRoute>
    );
  }

  return (
    <PrivateRoute>
      <ButtonAppBar />
      <Container maxWidth='md'>
        <Box component='main' sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1, sm: 2 } }}>
          <Typography variant='h4' component='h1' gutterBottom>
            Edit Recipe
          </Typography>
          <RecipeForm
            initialData={recipe}
            onSubmit={handleSubmit}
            submitLabel='Save Changes'
            submittingLabel='Saving...'
            onAutoSave={handleAutoSave}
            autoSaveEnabled={true}
            onImageUploaded={handleAutoSave}
          />
        </Box>
      </Container>
    </PrivateRoute>
  );
};

export default EditRecipe;
